import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard, getAdminOverview } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useLanguage } from '../../context/LanguageContext';
import { ENTRY_STATUS, formatAmount, formatDate, formatEur } from '../../lib/workStatus';
import {
  Users,
  Briefcase,
  FileText,
  Clock,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  Shield,
  ClipboardList,
  AlertTriangle,
  Wallet,
  Inbox
} from 'lucide-react';

const WorkOverview = ({ overview }) => {
  if (!overview) return null;
  const monthName = new Date(overview.year, overview.month - 1, 1).toLocaleString('lv', { month: 'long' });
  const stats = [
    { label: 'Šodien iesniegtas atskaites', value: overview.today_submitted, icon: Inbox, tone: 'text-[#1F3D2B]', to: '/portal/admin/work-entries', testId: 'overview-today' },
    { label: 'Neapstiprinātas atskaites', value: overview.pending_entries, icon: ClipboardList, tone: overview.pending_entries ? 'text-amber-600' : 'text-[#2E2E2E]', to: '/portal/admin/work-entries', testId: 'overview-pending' },
    { label: 'Sistēmas brīdinājumi', value: overview.warnings_count, icon: AlertTriangle, tone: overview.warnings_count ? 'text-red-600' : 'text-[#2E2E2E]', to: '#warnings', testId: 'overview-warnings' },
    { label: `Alga ${monthName} (apstiprināts)`, value: formatEur(overview.month_salary_total), icon: Wallet, tone: 'text-[#1F3D2B]', to: '/portal/admin/salary', testId: 'overview-salary' }
  ];
  return (
    <div className="space-y-4" data-testid="admin-work-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Link key={s.label} to={s.to} data-testid={s.testId}>
            <Card className="border-0 shadow-sm hover:shadow-lg transition-all h-full">
              <CardContent className="p-5 flex items-center justify-between">
                <div><p className="text-xs text-[#2E2E2E]/60">{s.label}</p><p className={`text-2xl font-bold mt-1 ${s.tone}`}>{s.value}</p></div>
                <s.icon className={`w-6 h-6 ${s.tone}`} />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm" id="warnings" data-testid="admin-overview-warnings-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-600" /> Brīdinājumi</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {overview.warnings.length === 0 ? <p className="text-sm text-[#2E2E2E]/60" data-testid="admin-overview-warnings-empty">Brīdinājumu nav — dati izskatās korekti.</p> : overview.warnings.map((w, i) => (
              <div key={i} className={`text-sm rounded-lg px-3 py-2 ${w.severity === 'high' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'}`} data-testid={`admin-overview-warning-${i}`}>{w.message}</div>
            ))}
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="admin-overview-employees-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Darbinieki — {monthName}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {overview.employees_month.length === 0 ? <p className="text-sm text-[#2E2E2E]/60" data-testid="admin-overview-employees-empty">Šomēnes vēl nav darba ierakstu.</p> : overview.employees_month.map((emp) => (
              <div key={emp.employee_id} className="flex items-center justify-between text-sm rounded-lg bg-[#f8faf8] px-3 py-2" data-testid={`admin-overview-employee-${emp.employee_id}`}>
                <div><p className="font-medium">{emp.employee_name}</p><p className="text-xs text-[#2E2E2E]/60">{Object.entries(emp.volumes).map(([u, v]) => formatAmount(v, u)).join(' · ') || '-'}{emp.pending ? ` · ${emp.pending} gaida` : ''}</p></div>
                <span className="font-semibold text-[#1F3D2B]">{formatEur(emp.approved_salary)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
      {overview.recent_entries.length > 0 && (
        <Card className="border-0 shadow-sm" data-testid="admin-overview-recent">
          <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-base">Pēdējās darba atskaites</CardTitle><Link to="/portal/admin/work-entries" className="text-sm text-[#1F3D2B] hover:underline">Visas</Link></div></CardHeader>
          <CardContent className="divide-y">
            {overview.recent_entries.map((e) => {
              const st = ENTRY_STATUS[e.status] || { label: e.status, className: '' };
              return (
                <div key={e.id} className="flex items-center justify-between py-2 text-sm" data-testid={`admin-overview-entry-${e.id}`}>
                  <div><span className="font-medium">{e.employee_name}</span> · {e.task_title} · {formatAmount(e.amount, e.unit)} <span className="text-[#2E2E2E]/50">({formatDate(e.date)})</span></div>
                  <Badge className={st.className}>{st.label}</Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { t } = useLanguage();
  const statusLabels = {
    planned: t('portalUi.status.planned', 'Plānots'),
    in_progress: t('portalUi.status.inProgress', 'Procesā'),
    completed: t('portalUi.status.completed', 'Pabeigts')
  };

  const statusColors = {
    planned: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700',
    completed: 'bg-green-100 text-green-700'
  };

  const [dashboard, setDashboard] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const [data, overviewData] = await Promise.all([getDashboard(), getAdminOverview().catch(() => null)]);
      setDashboard(data);
      setOverview(overviewData);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#1F3D2B] flex items-center justify-center">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#2E2E2E]" data-testid="admin-dashboard-title">
            <span className="gradient-text">{t('portalUi.admin.dashboard.title', 'Administrācijas')}</span> {t('portalUi.admin.dashboard.titleSuffix', 'panelis')}
          </h1>
          <p className="text-[#2E2E2E]/60" data-testid="admin-dashboard-subtitle">{t('portalUi.admin.dashboard.subtitlePrefix', 'Pārvaldiet')} <span className="text-[#1F3D2B] font-semibold">{t('portalUi.admin.dashboard.subtitleHighlight', 'klientus, darbus un dokumentus')}</span></p>
        </div>
      </div>

      <WorkOverview overview={overview} />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm hover:shadow-xl hover-lift card-shine transition-all" data-testid="admin-dashboard-clients-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.dashboard.stats.totalClients', 'Kopā klientu')}</p>
                <p className="text-3xl font-bold text-[#2E2E2E] mt-1" data-testid="admin-dashboard-clients-value">{dashboard?.total_clients || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#1F3D2B]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-xl hover-lift card-shine transition-all" data-testid="admin-dashboard-total-jobs-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.dashboard.stats.totalJobs', 'Kopā darbu')}</p>
                <p className="text-3xl font-bold text-[#2E2E2E] mt-1" data-testid="admin-dashboard-total-jobs-value">{dashboard?.total_jobs || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-xl hover-lift card-shine transition-all" data-testid="admin-dashboard-active-jobs-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.dashboard.stats.activeJobs', 'Aktīvie darbi')}</p>
                <p className="text-3xl font-bold text-amber-600 mt-1" data-testid="admin-dashboard-active-jobs-value">{dashboard?.active_jobs || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-xl hover-lift card-shine transition-all" data-testid="admin-dashboard-completed-jobs-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.dashboard.stats.completedJobs', 'Pabeigti')}</p>
                <p className="text-3xl font-bold text-green-600 mt-1" data-testid="admin-dashboard-completed-jobs-value">{dashboard?.completed_jobs || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Link to="/portal/admin/clients" data-testid="admin-dashboard-clients-link">
          <Card className="border-0 shadow-sm hover:shadow-xl hover:-translate-y-1 hover-lift card-shine transition-all cursor-pointer group h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center group-hover:bg-[#1F3D2B] transition-colors">
                <Users className="w-6 h-6 text-[#1F3D2B] group-hover:text-white transition-colors" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-[#2E2E2E]">{t('portalUi.admin.dashboard.quick.clientsTitle', 'Pārvaldīt klientus')}</h3>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.dashboard.quick.clientsDescription', 'Pievienot, rediģēt klientus')}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#2E2E2E]/40 group-hover:text-[#1F3D2B] transition-colors" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/portal/admin/jobs" data-testid="admin-dashboard-jobs-link">
          <Card className="border-0 shadow-sm hover:shadow-xl hover:-translate-y-1 hover-lift card-shine transition-all cursor-pointer group h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center group-hover:bg-[#1F3D2B] transition-colors">
                <Briefcase className="w-6 h-6 text-[#1F3D2B] group-hover:text-white transition-colors" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-[#2E2E2E]">{t('portalUi.admin.dashboard.quick.jobsTitle', 'Pārvaldīt darbus')}</h3>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.dashboard.quick.jobsDescription', 'Izveidot, atjaunināt darbus')}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#2E2E2E]/40 group-hover:text-[#1F3D2B] transition-colors" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/portal/admin/documents" data-testid="admin-dashboard-documents-link">
          <Card className="border-0 shadow-sm hover:shadow-xl hover:-translate-y-1 hover-lift card-shine transition-all cursor-pointer group h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center group-hover:bg-[#1F3D2B] transition-colors">
                <FileText className="w-6 h-6 text-[#1F3D2B] group-hover:text-white transition-colors" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-[#2E2E2E]">{t('portalUi.admin.dashboard.quick.documentsTitle', 'Dokumenti')}</h3>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.dashboard.quick.documentsDescription', 'Augšupielādēt dokumentus')}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#2E2E2E]/40 group-hover:text-[#1F3D2B] transition-colors" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Jobs */}
      <Card className="border-0 shadow-sm card-shine" data-testid="admin-dashboard-recent-jobs">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t('portalUi.admin.dashboard.recentTitle', 'Pēdējie darbi')}</CardTitle>
            <Link to="/portal/admin/jobs" className="text-sm text-[#1F3D2B] hover:underline font-medium" data-testid="admin-dashboard-recent-jobs-link">
              {t('portalUi.admin.dashboard.viewAll', 'Skatīt visus')}
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {dashboard?.recent_jobs?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.recent_jobs.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-[#f8faf8] hover:bg-[#1F3D2B]/5 transition-colors hover-lift"
                  data-testid={`admin-dashboard-recent-job-${job.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      job.status === 'completed' ? 'bg-green-500' :
                      job.status === 'in_progress' ? 'bg-amber-500' : 'bg-blue-500'
                    }`} />
                    <div>
                      <p className="font-medium text-[#2E2E2E]">{job.title}</p>
                      {job.responsible_person && (
                        <p className="text-sm text-[#2E2E2E]/60">{job.responsible_person}</p>
                      )}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[job.status]}`}>
                    {statusLabels[job.status]}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#2E2E2E]/60">
              <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t('portalUi.admin.dashboard.emptyJobs', 'Nav darbu')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
